import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import TextField from 'components/fields/TextField';
import FormError from 'components/common/FormError';
import AutosuggestField from 'components/fields/AutosuggestField';

import { requestSearchClaimAdjustmentCodes } from 'redux/modules/claimAdjustmentCodes';
import { updateEraAction, createEraAction } from 'redux/modules/eraActions';

import { accountLoginFail } from 'redux/modules/account';

import objectDiff from 'utils/objectDiff';

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function onChange(values, dispatch, props, prevValues) {
  if (values['Group ID']
    && values['Selected Group Name']
    && values['Group Name'] !== values['Selected Group Name']) {
    dispatch(props.change('Group ID', null));
  }
  if (values['Code ID']
    && values['Selected Code Name']
    && values['Code Name'] !== values['Selected Code Name']) {
    dispatch(props.change('Code ID', null));
  }
}

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {});
    if (normalizedValues['Group Name'] === '') {
      normalizedValues['Group ID'] = null;
    }
    if (normalizedValues['Code Name'] === '') {
      normalizedValues['Code ID'] = null;
    }
    delete normalizedValues['Group Name'];
    delete normalizedValues['Selected Group Name'];
    delete normalizedValues['Code Name'];
    delete normalizedValues['Selected Code Name'];
    
    return normalizedValues;
  };
  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateEraAction({
        values: {
          ID: props.ID,
          ...normalizeValues(diffValues),
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        throw new SubmissionError({ _error: err.error.indexOf('Unique check fail') !== -1
          ? 'Sorry, but such Claim Code already exists' : err.description || err.error });
      });
  }

  return new Promise((resolve, reject) => {
    dispatch(createEraAction({
      values: normalizeValues(values),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError({ _error: err.error.indexOf('Unique check fail') !== -1
        ? 'Sorry, but such ERA Action already exists' : err.description || err.error });
    });
}

const ClaimAdjustmentCodeCreateEditForm = ({
  handleSubmit,
  className,
  error,
  classes,
  change,
  actions,
  ID,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={6}
      >
        <Field
          id="Group Name"
          name="Group Name"
          component={AutosuggestField}
          label="Group"
          fullWidth
          renderSuggestionContent={suggestion => suggestion.Code}
          renderInputContent={suggestion => suggestion.Code}
          listWidth="auto"
          onSuggestionSelected={(suggestion) => {
            change('Group ID', suggestion.ID);
            change('Selected Group Name', suggestion.Code);
            return suggestion.Code;
          }}
          disabled={ID}
          fetchSuggestions={({ value, limit, offset }) => (
            requestSearchClaimAdjustmentCodes({
                filters: [
                  ...value.length > 0
                    ? [
                        {
                          name: 'Code',
                          operation: 'startswith',
                          val: value,
                          case_sensitivity: false,
                        },
                        {
                          name: 'Const Type',
                          operation: 'eq',
                          val: 'group',
                        },
                      ] : [
                        {
                          name: 'Const Type',
                          operation: 'eq',
                          val: 'group',
                        },
                      ],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Field
          id="Code Name"
          name="Code Name"
          component={AutosuggestField}
          label="Code"
          fullWidth
          renderSuggestionContent={suggestion => suggestion.Code}
          renderInputContent={suggestion => suggestion.Code}
          listWidth="auto"
          onSuggestionSelected={(suggestion) => {
            change('Code ID', suggestion.ID);
            change('Selected Code Name', suggestion.Code);
            return suggestion.Code;
          }}
          disabled={ID}
          fetchSuggestions={({ value, limit, offset }) => (
            requestSearchClaimAdjustmentCodes({
                filters: [
                  ...value.length > 0
                    ? [
                        {
                          name: 'Code',
                          operation: 'startswith',
                          val: value,
                          case_sensitivity: false,
                        },
                        {
                          name: 'Const Type',
                          operation: 'eq',
                          val: 'claim',
                        },
                      ] : [
                        {
                          name: 'Const Type',
                          operation: 'eq',
                          val: 'claim',
                        },
                      ],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Action"
          name="Action"
          label="Action"
          component={TextField}
          errorPlacement="top"
          fullWidth
        />
      </Grid>
    </Grid>
  </form>
);

ClaimAdjustmentCodeCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

ClaimAdjustmentCodeCreateEditForm.defaultProps = {
  className: '',
  error: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    accountLoginFail,
  }, dispatch),
});

export default reduxForm({
  form: 'claimAdjustmentCodeEdit',
  onSubmit,
  onChange,
})(connect(null, mapDispatchToProps)(withStyles(styles)(ClaimAdjustmentCodeCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/EraActionCreateEditDialog/EraActionCreateEditForm.js